import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiCheckCircle,
  FiClock,
  FiLoader,
  FiRefreshCw,
  FiSmartphone,
} from "react-icons/fi";
import { setAuthSession } from "../util/auth";
import {
  getApiErrorMessage,
  getSubscriptionStatusApi,
  loginApi,
} from "../http/http";
import classes from "./SubscriptionWaitingPage.module.css";

const POLL_INTERVAL_MS = 3000;
const CONFIRMATION_TIMEOUT_MS = 120000;

const copy = {
  eng: {
    waiting: "Waiting for subscription confirmation",
    instruction: "Check your phone and accept the operator subscription prompt.",
    checking: "Checking your subscription status...",
    retry: "Retry subscription request",
    retrying: "Sending a new subscription request...",
    timeoutTitle: "Confirmation is taking longer than expected",
    timeout: "Subscription confirmation was not received. Please try again.",
    success: "Subscription activated",
    successText: "Your subscription is active. Signing you in securely...",
  },
};

const readFlow = () => {
  try {
    return JSON.parse(sessionStorage.getItem("subscriptionFlow") || "null");
  } catch {
    return null;
  }
};

const SubscriptionWaitingPage = () => {
  const navigate = useNavigate();
  const t = copy.eng;
  const [flow, setFlow] = useState(readFlow);
  const [phase, setPhase] = useState("waiting");
  const [statusText, setStatusText] = useState("PENDING");
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState("");
  const finishedRef = useRef(false);

  const planLabel = useMemo(
    () => (flow?.packType ? `${flow.packType[0]}${flow.packType.slice(1).toLowerCase()}` : ""),
    [flow]
  );

  useEffect(() => {
    if (!flow?.packType || !flow?.msisdn) {
      navigate("/subscribe", { replace: true });
      return undefined;
    }

    let startedAt = Number(flow.startedAt) || Date.now();
    let pollingTimer;
    let successTimer;

    const returnToSubscription = (message) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      sessionStorage.removeItem("subscriptionFlow");
      navigate("/subscribe", { replace: true, state: { message } });
    };

    const checkStatus = async () => {
      if (finishedRef.current) return;
      if (Date.now() - startedAt >= CONFIRMATION_TIMEOUT_MS) {
        setStatusText("TIMEOUT");
        setPhase("timeout");
        window.clearInterval(pollingTimer);
        return;
      }

      try {
        const result = await getSubscriptionStatusApi({
          msisdn: flow.msisdn,
          packType: flow.packType,
        });
        const data = result?.data || {};
        const state = String(data.state || "PENDING").toUpperCase();
        setStatusText(state);

        if (data.isActive === true || state === "ACTIVE") {
          const loginResult = await loginApi({ msisdn: flow.msisdn });
          const { token, user } = loginResult?.data || {};
          if (!token) throw new Error("Application login token was not returned.");
          setAuthSession(token, user);
          finishedRef.current = true;
          sessionStorage.removeItem("subscriptionFlow");
          setPhase("success");
          successTimer = window.setTimeout(() => navigate("/", { replace: true }), 1600);
        } else if (state === "INACTIVE" || state === "FAILED") {
          returnToSubscription(
            data.message || data.reason || data.statusMessage ||
              `Subscription request failed with status: ${state}`
          );
        }
      } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          returnToSubscription(getApiErrorMessage(error, t.timeout));
        }
      }
    };

    checkStatus();
    pollingTimer = window.setInterval(checkStatus, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(pollingTimer);
      if (successTimer) window.clearTimeout(successTimer);
    };
  }, [flow, navigate, t.timeout]);

  const handleRetry = () => {
    if (retrying || !flow) return;
    setRetrying(true);
    setRetryError("");
    const nextFlow = { ...flow, startedAt: Date.now() };
    sessionStorage.setItem("subscriptionFlow", JSON.stringify(nextFlow));
    setFlow(nextFlow);
    setStatusText("PENDING");
    setPhase("waiting");
    setRetrying(false);
  };

  if (!flow) return null;

  return (
    <div className={classes.page}>
      <main className={classes.card}>
        {phase === "success" ? <>
          <div className={`${classes.statusIcon} ${classes.successIcon}`}><FiCheckCircle size={42} /></div>
          <h1>{t.success}</h1><p>{t.successText}</p><div className={classes.successBar} />
        </> : <>
          <div className={classes.phoneScene}><FiSmartphone size={56} /><span className={classes.phonePulse} /></div>
          <h1>{phase === "timeout" ? t.timeoutTitle : t.waiting}</h1>
          <p>{phase === "timeout" ? t.timeout : t.instruction}</p>
          <div className={classes.details}><div><span>MSISDN</span><strong>{flow.msisdn}</strong></div><div><span>Plan</span><strong>{planLabel}</strong></div></div>
          <div className={classes.checkingBox}><>{phase === "timeout" ? <FiClock size={20} /> : <FiLoader className={classes.spin} size={20} />}</><div><strong>{retrying ? t.retrying : t.checking}</strong><small><FiClock size={13} /> Status: {statusText}</small></div></div>
          {retryError && <div className={classes.retryError}>{retryError}</div>}
          {phase === "timeout" && <button type="button" className={classes.retryButton} onClick={handleRetry} disabled={retrying}><FiRefreshCw size={17} className={retrying ? classes.spin : ""} />{retrying ? t.retrying : t.retry}</button>}
        </>}
      </main>
    </div>
  );
};

export default SubscriptionWaitingPage;